// @flow
import React from 'react';
import classNames from 'classnames';
import { isFunction } from 'lodash';
import type { ToastData } from '../../../types';
import { Icon } from '../../elements';
import { createNsTranslator, isPresent } from '@helpers/helpers';
import { toastTimeout, RESUME_MIN_MS } from './toastTiming';
import './Toast.scss';
import { withTranslation } from 'react-i18next';

type Props = {
  toastData: ToastData,
  // ToastContainer is playing the exit animation: the toast is on its way out
  leaving?: boolean,
  onClose: Function,
  t: any,
};

type State = {
  dragX: number,
  dragging: boolean,
  // -1 or 1 once swiped away to that side
  swipedOut: number,
};

// A sideways drag this long (or a quick flick) dismisses the toast on a touch screen
const SWIPE_DISTANCE = 80;
const FLICK_DISTANCE = 30;
const FLICK_SPEED = 0.5; // px/ms

const isAlertType = (type: ?string): boolean => type === 'error' || type === 'warning';

// Each toast is its own instance: ToastContainer gives a new toast a new key, so it starts with its
// own timer. The timer pauses while the pointer is over the toast, a finger is on it or it has focus,
// and an error shown while offline closes as soon as the connection comes back.
class Toast extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { dragX: 0, dragging: false, swipedOut: 0 };
  }

  componentDidMount() {
    const { toastData } = this.props;
    // A toast that replaces another under a resting mouse: no pointerenter comes until it moves.
    // Only where hover is real: a phone keeps :hover on the last tapped spot.
    if (this.el && window.matchMedia && window.matchMedia('(hover: hover)').matches && this.el.matches(':hover')) {
      this.pausedBy.add('hover');
    }
    this.restartTimer();
    // Any error or warning shown while offline (reportError flags its own; others carry the API's
    // "check your internet" text) is about the connection: it goes when the connection is back
    if (toastData && (toastData.offline || (isAlertType(toastData.type) && window.navigator.onLine === false))) {
      window.addEventListener('online', this.handleOnline);
      // cordova-plugin-network-information fires it on document
      document.addEventListener('online', this.handleOnline);
    }
  }

  componentDidUpdate(prevProps: Props) {
    // The same toast again (another identical error): it stays, counting its time from now
    if (prevProps.toastData !== this.props.toastData) this.restartTimer();
    // Closed by its flow while the keyboard was in it
    if (this.props.leaving && !prevProps.leaving) this.restoreFocus();
  }

  componentWillUnmount() {
    this.clearTimer();
    window.removeEventListener('online', this.handleOnline);
    document.removeEventListener('online', this.handleOnline);
  }

  render() {
    const { toastData, leaving, t } = this.props;
    const { dragX, dragging, swipedOut } = this.state;
    const _ = createNsTranslator('toasts', t);

    if (!toastData) return null;

    const isAlert = isAlertType(toastData.type);
    const cssClasses = classNames('toast', toastData.type || 'info', {
      haveCloseButton: Boolean(toastData.close),
      leaving,
      dragging,
    });

    let style;
    if (swipedOut) style = { transform: `translateX(${swipedOut * 110}%)`, opacity: 0 };
    // Closed mid-drag: it fades from where the finger left it (inline style beats .leaving)
    else if (dragX) style = { transform: `translateX(${dragX}px)`, opacity: leaving ? 0 : Math.max(0.3, 1 - (Math.abs(dragX) / 240)) };

    return (
      <div
        ref={this.setRef}
        className={cssClasses}
        role={isAlert ? 'alert' : 'status'}
        style={style}
        onPointerEnter={this.handlePointerEnter}
        onPointerLeave={this.handlePointerLeave}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
        onTouchCancel={this.handleTouchEnd}
      >
        <span className={classNames('toastText', { multiline: toastData.multiline })}>{this.text()}</span>
        {isPresent(toastData.fileURL) && toastData.fileURL &&
          <button type="button" className="toastAction" onClick={this.handleOpenFile(toastData.fileURL)}>
            {_('label/view')}
          </button>
        }
        {toastData.close &&
          <button type="button" className="close" aria-label={_('label/close')} onClick={this.close}>
            <Icon iconName="close" size={20} />
          </button>
        }
      </div>
    );
  }

  el: ?HTMLElement = null;
  timeoutId: any = null;
  // Where the keyboard focus was before it came into the toast, to give it back on close
  returnFocusTo: ?HTMLElement = null;
  // ms left before it closes by itself; null when it waits for its flow (inProgress)
  remaining: ?number = null;
  startedAt: number = 0;
  pausedBy: Set<string> = new Set();
  touchStart: ?{ x: number, y: number, at: number } = null;
  // null until the finger moved enough to tell a sideways swipe (true) from a scroll (false)
  swiping: ?boolean = null;

  setRef = (el: ?HTMLElement) => { this.el = el; };

  text = (): string => {
    const { toastData, t } = this.props;
    return toastData.text ? toastData.text : createNsTranslator('toasts', t)(toastData.i18nText || 'unkdown_translation');
  };

  restartTimer = () => {
    this.clearTimer();
    this.remaining = toastTimeout(this.props.toastData, this.text());
    this.runTimer();
  };

  runTimer = () => {
    if (this.remaining == null || this.pausedBy.size > 0 || this.timeoutId) return;
    this.startedAt = Date.now();
    this.timeoutId = setTimeout(this.handleTimeout, this.remaining);
  };

  clearTimer = () => {
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = null;
  };

  pause = (reason: string) => {
    if (this.pausedBy.has(reason)) return;
    this.pausedBy.add(reason);
    if (this.timeoutId && this.remaining != null) {
      this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
      this.clearTimer();
    }
  };

  resume = (reason: string) => {
    if (!this.pausedBy.delete(reason) || this.pausedBy.size > 0 || this.remaining == null) return;
    // Not gone the instant the pointer leaves
    this.remaining = Math.max(this.remaining, RESUME_MIN_MS);
    this.runTimer();
  };

  close = () => {
    const { onClose, leaving } = this.props;
    this.clearTimer();
    if (leaving) return;
    this.restoreFocus();
    if (isFunction(onClose)) onClose();
  };

  restoreFocus = () => {
    const { el, returnFocusTo } = this;
    if (!el || !returnFocusTo || !el.contains(document.activeElement)) return;
    if (document.body && document.body.contains(returnFocusTo)) returnFocusTo.focus();
  };

  handleTimeout = () => {
    this.timeoutId = null;
    this.close();
  };

  handleOnline = () => this.close();

  // Hover is a mouse thing: a tap also sends pointerenter, and no pointerleave until the next tap elsewhere
  handlePointerEnter = (e: Object) => {
    if (e.pointerType !== 'touch') this.pause('hover');
  };

  handlePointerLeave = (e: Object) => {
    if (e.pointerType !== 'touch') this.resume('hover');
  };

  handleFocus = (e: Object) => {
    this.pause('focus');
    const from = e.relatedTarget;
    if (from && this.el && !this.el.contains(from)) this.returnFocusTo = from;
  };

  handleBlur = () => this.resume('focus');

  // The toast's touches are its own: PatientTimeline swipes its side panels on document-level touches
  handleTouchStart = (e: Object) => {
    e.stopPropagation();
    const touch = e.touches[0];
    if (!touch) return;
    this.pause('touch');
    this.touchStart = { x: touch.clientX, y: touch.clientY, at: Date.now() };
    this.swiping = null;
  };

  handleTouchMove = (e: Object) => {
    e.stopPropagation();
    const touch = e.touches[0];
    if (!this.touchStart || !touch || this.state.swipedOut) return;
    const dx = touch.clientX - this.touchStart.x;
    const dy = touch.clientY - this.touchStart.y;
    if (this.swiping == null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) this.swiping = Math.abs(dx) > Math.abs(dy);
    if (this.swiping) this.setState({ dragX: dx, dragging: true });
  };

  handleTouchEnd = (e: Object) => {
    e.stopPropagation();
    // Another finger is still on it
    if (e.touches && e.touches.length > 0) return;
    const start = this.touchStart;
    const { dragX } = this.state;
    this.touchStart = null;
    if (this.state.swipedOut) return;
    const elapsed = start ? Math.max(1, Date.now() - start.at) : 1;
    const flick = Math.abs(dragX) >= FLICK_DISTANCE && Math.abs(dragX) / elapsed >= FLICK_SPEED;
    if (this.swiping && (Math.abs(dragX) >= SWIPE_DISTANCE || flick)) {
      // It slides out to that side during ToastContainer's exit
      this.setState({ dragging: false, swipedOut: dragX < 0 ? -1 : 1 });
      this.close();
      return;
    }
    // Not far enough: it springs back and the timer goes on
    if (dragX) this.setState({ dragX: 0, dragging: false });
    this.resume('touch');
  };

  handleOpenFile = (file: string) => () => {
    // On its way out it no longer acts (pointer-events: none doesn't stop Enter)
    if (this.props.leaving) return;
    window.open(file, '_blank');
    this.close();
  };
}

export default withTranslation()(Toast);
